import React, { useCallback, useEffect, useState, useRef } from 'react';
import { HomePage } from './pages/HomePage';
import { ReportConcernPage } from './pages/ReportConcernPage';
import { FoodSafetyPage } from './pages/FoodSafetyPage';
import { EnvironmentalHealthPage } from './pages/EnvironmentalHealthPage';
import { FoodComplaintPage } from './pages/FoodComplaintPage';
// Only truly functional routes
const LIVE_HASHES = ['#report', '#report-food', '#report-food-complaint', '#report-environmental'];
function DemoPrompt({
  onClose
}: {
  onClose: () => void;
}) {
  return <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-4 pointer-events-none" data-id="element-0">
      <div className="pointer-events-auto bg-gov-navy text-white rounded-2xl shadow-2xl p-6 sm:p-8 max-w-md w-full border border-white/10 animate-[slideUp_0.3s_ease-out]" role="alert" data-id="element-1">
        <div className="text-3xl mb-3" data-id="element-2">📋</div>
        <p className="text-gray-300 mb-6 leading-relaxed text-lg" data-id="element-3">
          This feature isn't part of the demo. Try our{' '}
          <strong className="text-white" data-id="element-4">Report a Public Health Concern</strong>{' '}
          flow to see the full experience. →
        </p>
        <div className="flex flex-col sm:flex-row gap-3" data-id="element-5">
          <a href="#report" onClick={onClose} className="flex-1 text-center bg-gov-amber hover:bg-gov-amberHover text-gov-navyDark font-bold py-3 px-6 rounded-lg transition-colors text-lg" data-id="element-6">
            Try it now →
          </a>
          <button onClick={onClose} className="flex-1 text-center bg-white/10 hover:bg-white/20 text-white font-medium py-3 px-6 rounded-lg transition-colors" data-id="element-7">
            Dismiss
          </button>
        </div>
      </div>
    </div>;
}
export function App() {
  const [page, setPage] = useState(window.location.hash);
  const [showPrompt, setShowPrompt] = useState(false);
  const lastPromptTime = useRef(0);
  useEffect(() => {
    document.title = 'Demo County Website by Dinesafe';
  }, []);
  useEffect(() => {
    const onHashChange = () => {
      setPage(window.location.hash);
      window.scrollTo(0, 0);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);
  // Intercept dead links and buttons
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const el = e.target as HTMLElement;
      // Check for anchor tags
      const anchor = el.closest('a');
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        // If it's a live route, let it through
        if (LIVE_HASHES.includes(href)) return;
        // If it's the Home link (href="#home" with onClick handler), let it through
        if (href === '#home') return;
        // Everything else is a dead link
        if (href.startsWith('#')) {
          e.preventDefault();
          e.stopPropagation();
          const now = Date.now();
          if (now - lastPromptTime.current < 3000) return;
          lastPromptTime.current = now;
          setShowPrompt(true);
          return;
        }
      }
      // Check for buttons (like Search)
      const button = el.closest('button');
      if (button && !button.closest('nav')?.querySelector('[aria-label="Toggle menu"]')) {
        const ariaLabel = button.getAttribute('aria-label');
        if (ariaLabel === 'Search') {
          e.preventDefault();
          const now = Date.now();
          if (now - lastPromptTime.current < 3000) return;
          lastPromptTime.current = now;
          setShowPrompt(true);
        }
      }
    };
    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, []);
  // Auto-dismiss after 6 seconds
  useEffect(() => {
    if (!showPrompt) return;
    const timer = setTimeout(() => setShowPrompt(false), 6000);
    return () => clearTimeout(timer);
  }, [showPrompt]);
  const handleClose = useCallback(() => setShowPrompt(false), []);
  const prompt = showPrompt ? <DemoPrompt onClose={handleClose} data-id="element-8" /> : null;
  if (page === '#report') {
    return <>
        {prompt}
        <ReportConcernPage data-id="element-9" />
      </>;
  }
  if (page === '#report-food') {
    return <>
        {prompt}
        <FoodSafetyPage data-id="element-10" />
      </>;
  }
  if (page === '#report-food-complaint') {
    return <>
        {prompt}
        <FoodComplaintPage data-id="element-11" />
      </>;
  }
  if (page === '#report-environmental') {
    return <>
        {prompt}
        <EnvironmentalHealthPage data-id="element-12" />
      </>;
  }
  return <>
      {prompt}
      <HomePage data-id="element-13" />
    </>;
}